import type { jsPDF } from 'jspdf';
import type { CellDef, CellHookData, CellInput, RowInput, Styles, UserOptions } from 'jspdf-autotable';
import { ensureEmbeddedFont, registerEmbeddedFont } from '../pdf/embeddedFont';
import { risikoStufe } from './gbuLogic';
import type {
  GbuAnlass,
  GbuFassungInhalt,
  GbuInhaltGefaehrdung,
  GbuRolle,
  GbuUnterschrift,
  MassnahmeArt,
  MassnahmeListenZeile,
  RisikoStufe,
  UnterschriftRolle,
  VorsorgeArt,
} from './gbuTypes';

/**
 * Gefaehrdungsbeurteilung (GBU) als PDF, A4 quer — und die Massnahmenliste.
 *
 * Quelle ist IMMER der eingefrorene Inhalt einer Fassung (`GbuFassungInhalt`,
 * gebaut von gbuLogic.baueFassungsInhalt) bzw. die fertig gerechnete
 * Massnahmenliste (`MassnahmeListenZeile[]`). Hier wird nichts nachgeschlagen
 * und nichts fachlich gerechnet — dasselbe Blatt entsteht im Editor, in der
 * Ablage, im Kundenportal und auf der oeffentlichen Freigabeseite.
 *
 * Geruest wie cockpit/cockpitPdf.ts (quer, jspdf-autotable, beides erst beim
 * Aufruf nachgeladen), Konventionen wie sifa/taetigkeitsnachweisPdf.ts: reine
 * Funktion, Blob raus, kein `doc.save()`, eingebettete Schrift, „Seite i von
 * n". Das Briefpapier (letterhead.ts) kennt nur Hochformat; Stufe 1 hat
 * deshalb einen eigenen schlichten Kopf und Fuss.
 *
 * Inhalt und Spaltenfolge folgen dem Excel-Muster des Betreibers (Plan §7),
 * damit Kunden ihre GBU wiedererkennen; die Optik weicht bewusst ab.
 *
 * Die Aufbereitung (Labels, Datum, Freigabefelder, Dateinamen) ist rein und
 * exportiert — gbuExcel.ts benutzt dieselben Texte, damit PDF und Excel nie
 * auseinanderlaufen.
 */

// ─── Optionen ───────────────────────────────────────────────────────────────

export interface GbuAusgabeOptionen {
  /** Unterschriften der Fassung (`sifa_gbu_fassungen.unterschriften`). */
  unterschriften?: readonly GbuUnterschrift[];
  /**
   * Papierweg: Rollen ohne Unterschrift bekommen leere Linien fuer Datum und
   * Unterschrift (Name und Funktion bleiben vorbelegt). Schon vorhandene
   * Unterschriften werden trotzdem gezeigt.
   */
  leereUnterschriftsfelder?: boolean;
  /** „ENTWURF" schraeg ueber jede Seite (PDF) bzw. in Kopf und Kopfzeile (Excel). */
  entwurf?: boolean;
}

export interface MassnahmenlisteMeta {
  /** Betrieb, dessen Massnahmen die Liste zeigt. */
  kundeName: string;
  bueroName?: string | null;
  /** Stichtag (yyyy-mm-dd), an dem `ueberfaellig` gerechnet wurde — erscheint als „Stand". */
  stand: string;
  /** Optionaler Zusatz unter der Ueberschrift, z. B. ein Filter („nur offene"). */
  untertitel?: string | null;
}

// ─── Texte (gemeinsam mit gbuExcel.ts) ──────────────────────────────────────

export const ANLASS_LABEL: Record<GbuAnlass, string> = {
  ersterstellung: 'Ersterstellung',
  aenderung: 'Änderung',
  ereignis: 'Ereignis (Unfall, Beinaheunfall, Erkrankung)',
  regelpruefung: 'Regelmäßige Überprüfung',
};

export const ART_LABEL: Record<MassnahmeArt, string> = {
  S: 'Substitution',
  T: 'Technisch',
  O: 'Organisatorisch',
  P: 'Personenbezogen',
};

/** Langtexte der STOP-Rangfolge fuer die Legende. */
export const ART_ERKLAERUNG: Record<MassnahmeArt, string> = {
  S: 'Substitution: Gefahrenquelle beseitigen oder ersetzen',
  T: 'Technische Maßnahmen',
  O: 'Organisatorische Maßnahmen',
  P: 'Personenbezogene Maßnahmen (PSA, Unterweisung)',
};

export const STOP_HINWEIS =
  'Rangfolge nach § 4 ArbSchG: S vor T vor O vor P. Personenbezogene Maßnahmen ergänzen, ersetzen aber keine vorrangige Maßnahme.';

export const VORSORGE_ART_LABEL: Record<VorsorgeArt, string> = {
  pflicht: 'Pflichtvorsorge',
  angebot: 'Angebotsvorsorge',
  wunsch: 'Wunschvorsorge',
};

export const STAND_LABEL: Record<MassnahmeListenZeile['stand'], string> = {
  offen: 'offen',
  gemeldet: 'umgesetzt gemeldet',
  wirksam: 'wirksam',
  unwirksam: 'unwirksam',
};

export const ROLLEN_TITEL: Record<UnterschriftRolle, string> = {
  erstellt: 'Erstellt',
  geprueft: 'Geprüft',
  freigegeben: 'Freigegeben',
};

export const ROLLEN_HINWEIS: Record<UnterschriftRolle, string> = {
  erstellt: '',
  geprueft: 'optional',
  freigegeben: 'Unternehmer bzw. verantwortliche Führungskraft',
};

/** Stufen der 3×3-Matrix (Index 0 = Stufe 1). */
export const W_STUFEN = ['unwahrscheinlich', 'möglich', 'wahrscheinlich'] as const;
export const S_STUFEN = ['leicht', 'mittel', 'schwer'] as const;
export const S_ERKLAERUNG = [
  'leicht: Erste Hilfe, kein Ausfall',
  'mittel: Arbeitsunfähigkeit',
  'schwer: bleibender Schaden oder Tod',
] as const;

export const RISIKO_LEGENDE: Record<RisikoStufe, { bereich: string; text: string }> = {
  gering: { bereich: '1–2', text: 'gering – vertretbar' },
  mittel: { bereich: '3–4', text: 'mittel – Maßnahmen erforderlich' },
  hoch: { bereich: '6–9', text: 'hoch – Maßnahmen sofort erforderlich' },
};

type Rgb = [number, number, number];

/** Ampelfarben der Risikostufen, als RGB (PDF) und ARGB (Excel). */
export const RISIKO_FARBE: Record<RisikoStufe, { rgb: Rgb; argb: string }> = {
  gering: { rgb: [134, 239, 172], argb: 'FF86EFAC' },
  mittel: { rgb: [253, 224, 71], argb: 'FFFDE047' },
  hoch: { rgb: [248, 113, 113], argb: 'FFF87171' },
};

/** Kennfarbe je Massnahmenart (Marke vor der Massnahme). */
export const ART_FARBE: Record<MassnahmeArt, { rgb: Rgb; argb: string }> = {
  S: { rgb: [124, 58, 237], argb: 'FF7C3AED' },
  T: { rgb: [37, 99, 235], argb: 'FF2563EB' },
  O: { rgb: [13, 148, 136], argb: 'FF0D9488' },
  P: { rgb: [217, 119, 6], argb: 'FFD97706' },
};

// ─── Reine Aufbereitung ─────────────────────────────────────────────────────

const t = (v: string | null | undefined): string => (v ?? '').toString().trim();

/** Mehrfache Leerzeichen zusammenfassen, Zeilenumbrueche behalten. */
export const textNorm = (v: string | null | undefined): string =>
  t(v).replace(/\r\n?/g, '\n').replace(/[ \t]+/g, ' ').replace(/ ?\n ?/g, '\n').replace(/\n{3,}/g, '\n\n');

/**
 * R = W × S → Stufe (1–2 gering, 3–4 mittel, 6–9 hoch) ueber
 * gbuLogic.risikoStufe — die Grenzen stehen nur dort. Unbewertet (0, NaN) →
 * null, die Zelle bleibt dann ungefaerbt.
 */
export const risikoStufeVon = (r: number | null | undefined): RisikoStufe | null => {
  if (typeof r !== 'number' || !Number.isFinite(r) || r < 1) return null;
  return risikoStufe(r);
};

const NUR_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIT_ZONE = /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})$/i;
const BERLIN = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Kalendertag (yyyy-mm-dd) eines Datums oder Zeitstempels. Ein reines Datum
 * bleibt Zeichenkette (kein `new Date`, keine Zeitzonenverschiebung); ein
 * Zeitstempel mit Zone (Einfrieren, Unterschrift) zaehlt als Berliner Tag —
 * 22:30 UTC am 20.10. ist in Berlin schon der 21.10.
 */
export const berlinerTag = (wert: string | null | undefined): string | null => {
  const s = t(wert);
  if (!s) return null;
  if (NUR_DATUM.test(s)) return s;
  if (MIT_ZONE.test(s)) {
    const ms = Date.parse(s);
    if (Number.isFinite(ms)) {
      const teile = BERLIN.formatToParts(ms);
      const teil = (typ: string) => teile.find((p) => p.type === typ)?.value ?? '';
      return `${teil('year')}-${teil('month')}-${teil('day')}`;
    }
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** „2026-10-20" bzw. Zeitstempel → „20.10.2026"; Unbekanntes bleibt, wie es ist. */
export const datumDe = (wert: string | null | undefined): string => {
  const tag = berlinerTag(wert);
  if (!tag) return t(wert);
  const [y, m, d] = tag.split('-');
  return `${d}.${m}.${y}`;
};

/** Termin einer Massnahme: Datum oder „laufend". */
export const terminText = (termin: string | null | undefined): string => datumDe(termin) || 'laufend';

const zahlDe = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

/**
 * Massnahmentext mit PSA-Bezeichnung. Steht die PSA schon im Text („Gehoerschutz
 * tragen"), wird sie nicht doppelt angehaengt.
 */
export const massnahmeText = (m: { text: string; psaLabel: string | null }): string => {
  const text = textNorm(m.text);
  const psa = t(m.psaLabel);
  if (!psa) return text;
  if (text.toLowerCase().includes(psa.toLowerCase())) return text;
  return text ? `${text} (PSA: ${psa})` : `PSA: ${psa}`;
};

/** „Laerm (87 dB(A))" — der Anlass mit Messwert, ohne die Art. */
export const vorsorgeAnlassText = (v: GbuInhaltGefaehrdung['vorsorge'][number]): string => {
  const label = textNorm(v.anlassLabel) || t(v.anlassCode);
  if (typeof v.messwert !== 'number' || !Number.isFinite(v.messwert)) return label;
  return `${label} (${zahlDe(v.messwert)}${t(v.einheit) ? ` ${t(v.einheit)}` : ''})`;
};

/** Kurzform fuer eine Zelle: „Pflichtvorsorge: Laerm (87 dB(A))". */
export const vorsorgeText = (v: GbuInhaltGefaehrdung['vorsorge'][number]): string =>
  `${VORSORGE_ART_LABEL[v.art] ?? v.art}: ${vorsorgeAnlassText(v)}`;

/** Bezug ins Kataster und Vorschriften einer Gefaehrdung als Zusatzzeilen. */
export const gefaehrdungZusatz = (g: GbuInhaltGefaehrdung): string[] => {
  const zeilen: string[] = [];
  const liste = (xs: readonly string[] | null | undefined) => (xs ?? []).map(t).filter(Boolean).join(', ');
  if (liste(g.arbeitsmittel)) zeilen.push(`Arbeitsmittel: ${liste(g.arbeitsmittel)}`);
  if (liste(g.stoffe)) zeilen.push(`Stoffe: ${liste(g.stoffe)}`);
  if (liste(g.vorschriften)) zeilen.push(`Vorschriften: ${liste(g.vorschriften)}`);
  return zeilen;
};

// ─── Silbentrennung fuer schmale Spalten ────────────────────────────────────

const VOKAL = /[aeiouäöüy]/i;
const BUCHSTABE = /\p{L}/u;
/** Endungen, nach denen ein Wortteil im Kompositum endet („Arbeits|umgebungs|bedingungen"). */
const FUGE = /(?:ungs|heits|keits|ions|ts|gs)$/i;
/** Vor diesem „s" wird nicht getrennt: es ist ein Fugen-s und gehoert nach links. */
const FUGEN_S_LINKS = /(?:ung|heit|keit|ion|t|g)$/i;

/**
 * Moegliche Trennstellen eines Wortes (Index i = Trennung VOR wort[i]):
 * nach einem vorhandenen Bindestrich, nach typischen Fugen und nach der
 * einfachen Silbenregel „vor einem Konsonanten, dem ein Vokal folgt" (ch, ck,
 * ph, th und sch bleiben zusammen). Kein Woerterbuch — es geht nur darum,
 * dass jsPDF lange Fachwoerter in schmalen Spalten nicht mitten im Wort ohne
 * Strich zerreisst („Arbeitsumgebu|ngsbedingungen").
 */
export const trennstellen = (wort: string): number[] =>
  trennKandidaten(wort).map((k) => k.p);

/** Wie `trennstellen`, dazu `stark` = Bindestrich oder Fuge (Grenze zwischen zwei Wortteilen). */
const trennKandidaten = (wort: string): Array<{ p: number; stark: boolean }> => {
  const stark = new Set<number>();
  const stellen = new Set<number>();
  const klein = wort.toLowerCase();
  for (let i = 1; i < wort.length; i += 1) {
    if (wort[i - 1] === '-' || wort[i - 1] === '/') { stellen.add(i); stark.add(i); }
  }
  for (let i = 2; i <= wort.length - 2; i += 1) {
    if (FUGE.test(wort.slice(0, i)) && BUCHSTABE.test(wort[i])) { stellen.add(i); stark.add(i); }
    const c = klein[i];
    if (!BUCHSTABE.test(c) || VOKAL.test(c) || !VOKAL.test(klein[i + 1] ?? '')) continue;
    let p = i;
    if ((c === 'h' && 'cpt'.includes(klein[i - 1])) || (c === 'k' && klein[i - 1] === 'c')) {
      p = i - 1;
      if (klein[i - 1] === 'c' && klein[i - 2] === 's') p = i - 2;
    }
    if (p < 2 || wort.length - p < 2) continue;
    if (!BUCHSTABE.test(wort[p - 1]) || !VOKAL.test(wort.slice(0, p))) continue;
    if (klein[p] === 's' && FUGEN_S_LINKS.test(wort.slice(0, p))) continue;
    stellen.add(p);
  }
  return [...stellen].sort((a, b) => a - b).map((p) => ({ p, stark: stark.has(p) }));
};

/**
 * Ein Wort, das breiter als `breite` ist, in Stuecke mit Trennstrich teilen.
 * Bevorzugt wird die laengste passende Wortteilgrenze (Bindestrich, Fuge),
 * sofern das Stueck mindestens 40 % der Breite fuellt; sonst die laengste
 * passende Silbengrenze, notfalls hart. `mass` misst eine Zeichenkette
 * (jsPDF getTextWidth). Passt das Wort, kommt es unveraendert zurueck.
 */
export const trenneWort = (wort: string, breite: number, mass: (s: string) => number): string[] => {
  const teile: string[] = [];
  let rest = wort;
  let sicherung = 0;
  while (mass(rest) > breite && rest.length > 1 && sicherung < 50) {
    sicherung += 1;
    const passt = (p: number) => {
      const stueck = rest.slice(0, p);
      return mass(/[-/]$/.test(stueck) ? stueck : `${stueck}-`) <= breite;
    };
    const kandidaten = trennKandidaten(rest).filter((k) => k.p < rest.length && passt(k.p));
    const starke = kandidaten.filter((k) => k.stark && mass(rest.slice(0, k.p)) >= breite * 0.4);
    let p = (starke.length ? starke : kandidaten).at(-1)?.p ?? 0;
    if (!p) {
      p = rest.length - 1;
      while (p > 1 && !passt(p)) p -= 1;
    }
    const stueck = rest.slice(0, p);
    teile.push(/[-/]$/.test(stueck) ? stueck : `${stueck}-`);
    rest = rest.slice(p);
  }
  teile.push(rest);
  return teile;
};

/** Nummern der Gefaehrdungen, deren Risiko NACH den Massnahmen hoch bleibt (O4: hervorheben, nicht sperren). */
export const restrisikoHoch = (inhalt: Pick<GbuFassungInhalt, 'gefaehrdungen'>): number[] =>
  (inhalt.gefaehrdungen ?? []).filter((g) => risikoStufeVon(g.rNach) === 'hoch').map((g) => g.nr);

export interface FreigabeFeld {
  rolle: UnterschriftRolle;
  titel: string;
  hinweis: string;
  name: string;
  funktion: string;
  /** dd.mm.yyyy; leer, solange nicht unterschrieben. */
  datum: string;
  /** PNG/JPEG-DataURL der Unterschrift oder null. */
  bild: string | null;
  /**
   * 'unterschrieben' = Unterschrift liegt vor; 'leer' = Papierweg, Linien zum
   * Ausfuellen; 'offen' = noch nicht unterschrieben; 'entfaellt' = Pruefung
   * nicht vorgesehen (nur „Gepruft" ohne Namen).
   */
  zustand: 'unterschrieben' | 'leer' | 'offen' | 'entfaellt';
  weg: GbuUnterschrift['weg'] | null;
}

const ROLLEN: readonly UnterschriftRolle[] = ['erstellt', 'geprueft', 'freigegeben'];

/** Die drei Felder des Freigabeblocks (Abschnitt 7). Bei mehreren Unterschriften einer Rolle gilt die letzte. */
export const freigabeFelder = (inhalt: Pick<GbuFassungInhalt, 'rollen'>, opt: GbuAusgabeOptionen = {}): FreigabeFeld[] =>
  ROLLEN.map((rolle): FreigabeFeld => {
    const u = [...(opt.unterschriften ?? [])].reverse().find((x) => x?.rolle === rolle) ?? null;
    const vorgabe: GbuRolle | null = (inhalt.rollen?.[rolle] as GbuRolle | null | undefined) ?? null;
    const name = t(u?.name) || t(vorgabe?.name);
    const funktion = t(u?.funktion) || t(vorgabe?.funktion);
    const bild = u && /^data:image\/(png|jpe?g);base64,/i.test(t(u.bild)) ? t(u.bild) : null;
    let zustand: FreigabeFeld['zustand'];
    // „Entfaellt" vor „leer": ohne vorgesehenen Pruefer bekommt auch das
    // Papier-PDF keine Unterschriftslinie fuer „Geprueft".
    if (u) zustand = 'unterschrieben';
    else if (rolle === 'geprueft' && !name) zustand = 'entfaellt';
    else if (opt.leereUnterschriftsfelder) zustand = 'leer';
    else zustand = 'offen';
    return {
      rolle,
      titel: ROLLEN_TITEL[rolle],
      hinweis: ROLLEN_HINWEIS[rolle],
      name,
      funktion,
      datum: u ? datumDe(u.datum) : '',
      bild,
      zustand,
      weg: u?.weg ?? null,
    };
  });

export const WEG_TEXT: Record<GbuUnterschrift['weg'], string> = {
  geraet: 'digital am Gerät unterschrieben',
  link: 'digital per Freigabe-Link unterschrieben',
  papier: 'auf Papier unterschrieben (Scan bei der Fassung)',
};

const dateiTeil = (v: string): string =>
  t(v).normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 60);

/** „GBU-3-F2-Einschalen.pdf" */
export const gbuDateiname = (inhalt: Pick<GbuFassungInhalt, 'nummer' | 'fassung' | 'titel'>, endung: 'pdf' | 'xlsx'): string => {
  const titel = dateiTeil(inhalt.titel);
  return `GBU-${inhalt.nummer}-F${inhalt.fassung}${titel ? `-${titel}` : ''}.${endung}`;
};

/** „Massnahmenliste-Musterbau-GmbH-2026-10-20.xlsx" */
export const massnahmenlisteDateiname = (meta: Pick<MassnahmenlisteMeta, 'kundeName' | 'stand'>, endung: 'pdf' | 'xlsx'): string => {
  const kunde = dateiTeil(meta.kundeName);
  const tag = berlinerTag(meta.stand) ?? '';
  return ['Massnahmenliste', kunde, tag].filter(Boolean).join('-') + `.${endung}`;
};

// ─── Zeichnen: Grundlagen ───────────────────────────────────────────────────

const SEITE_B = 297;
const SEITE_H = 210;
const RAND = 10;
const BREITE = SEITE_B - 2 * RAND;
/** Folgeseiten: Inhalt beginnt unter dem Laufkopf. */
const OBEN_FOLGESEITE = 15;
/** Platz fuer die Fusszeile. */
const UNTEN = 14;
const INHALT_UNTEN = SEITE_H - UNTEN;

const TINTE: Rgb = [15, 23, 42];
const TEXT: Rgb = [30, 41, 59];
const GRAU: Rgb = [100, 116, 139];
const HELLGRAU: Rgb = [148, 163, 184];
/**
 * Alle Gitter- und Rahmenlinien in EINEM neutralen Hellgrau (wie gbuExcel.ts,
 * Praxistest 20.10.2026: keine farbigen Trennlinien). `LINIE_DUNKEL` nur
 * fuer Linien zum Ausfuellen (Papierweg), damit man darauf schreiben kann.
 */
const LINIE: Rgb = [200, 200, 200];
const LINIE_DUNKEL: Rgb = [140, 140, 140];
const AKZENT: Rgb = [13, 148, 136];
const KOPF_FUELLUNG: Rgb = [30, 41, 59];
const LABEL_FUELLUNG: Rgb = [246, 246, 246];
const ROT_TEXT: Rgb = [185, 28, 28];
const ROT_HELL: Rgb = [254, 242, 242];

type AutoTable = (doc: jsPDF, options: UserOptions) => void;
type GStateKlasse = new (p: { opacity?: number }) => unknown;

/** jspdf und jspdf-autotable erst beim Aufruf — zusammen groesser als der halbe Anwendungscode. */
const ladePdf = async (): Promise<{ doc: jsPDF; autoTable: AutoTable; GState: GStateKlasse }> => {
  const [mod, at, schrift] = await Promise.all([import('jspdf'), import('jspdf-autotable'), ensureEmbeddedFont()]);
  const doc = new mod.jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  // Eingebettete Liberation Sans (Umlaute, „·", „–" sicher); ohne sie Helvetica.
  if (schrift) registerEmbeddedFont(doc);
  return { doc, autoTable: at.default as unknown as AutoTable, GState: mod.GState as unknown as GStateKlasse };
};

const endY = (doc: jsPDF, ersatz: number): number =>
  (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ersatz;

const setzeText = (doc: jsPDF, groesse: number, stil: 'normal' | 'bold' | 'italic', farbe: Rgb) => {
  doc.setFont('helvetica', stil);
  doc.setFontSize(groesse);
  doc.setTextColor(farbe[0], farbe[1], farbe[2]);
};

/**
 * Mit Schrift und Groesse der Zelle umbrechen — autoTable teilt die Zeilen
 * dann nicht weiter. Woerter, die breiter als die Spalte sind, werden vorher
 * mit Trennstrich geteilt (`trenneWort`).
 */
const umbrechen = (doc: jsPDF, text: string, breite: number, groesse: number, stil: 'normal' | 'bold' = 'normal'): string[] => {
  if (!text) return [''];
  doc.setFont('helvetica', stil);
  doc.setFontSize(groesse);
  const mass = (s: string) => doc.getTextWidth(s);
  const getrennt = text
    .split('\n')
    .map((absatz) => absatz.split(' ').map((wort) => (mass(wort) > breite ? trenneWort(wort, breite, mass).join(' ') : wort)).join(' '))
    .join('\n');
  return doc.splitTextToSize(getrennt, breite) as string[];
};

/** Zellinhalt vorab umbrochen (Zeilen mit \n), damit lange Woerter getrennt statt zerrissen werden. */
const umbrochen = (doc: jsPDF, text: string, spalte: number, groesse: number, stil: 'normal' | 'bold' = 'normal', pad = 1.2): string =>
  umbrechen(doc, text, spalte - 2 * pad - 0.2, groesse, stil).join('\n');

const bildFormat = (dataUrl: string): 'PNG' | 'JPEG' | null => {
  if (/^data:image\/png;base64,/i.test(dataUrl)) return 'PNG';
  if (/^data:image\/jpe?g;base64,/i.test(dataUrl)) return 'JPEG';
  return null;
};

/**
 * „Reiche" Zellen: autoTable kennt je Zelle nur einen Stil. Fuer Marken
 * (S/T/O/P) und graue Zusatzzeilen wird der Text vorab umbrochen und als
 * Zeilenliste hinterlegt; autoTable misst damit die Hoehe exakt, gezeichnet
 * wird aber selbst (willDrawCell leert den Text, didDrawCell schreibt die
 * Zeilen). Wird eine sehr hohe Zeile ueber eine Seite geteilt, findet die
 * Suche den Ausschnitt dieser Seite; passt nichts, zeichnet autoTable den
 * Text wie gewohnt (nur ohne Marken) — lieber schlicht als verloren.
 */
interface ReichZeile {
  text: string;
  ton: 'normal' | 'grau' | 'fett';
  marke?: MassnahmeArt;
}

const ausschnittAb = (alle: readonly string[], teil: readonly string[]): number => {
  if (teil.length === 0) return 0;
  for (let k = 0; k + teil.length <= alle.length; k += 1) {
    let gleich = true;
    for (let i = 0; i < teil.length; i += 1) {
      if (alle[k + i] !== teil[i]) { gleich = false; break; }
    }
    if (gleich) return k;
  }
  return -1;
};

const reicheZellen = (doc: jsPDF) => {
  const hinterlegt = new WeakMap<object, ReichZeile[]>();
  const zuZeichnen = new WeakMap<object, ReichZeile[]>();

  const zelle = (zeilen: ReichZeile[], styles?: Partial<Styles>): CellDef => {
    const def: CellDef = { content: zeilen.map((z) => z.text).join('\n'), styles };
    hinterlegt.set(def, zeilen);
    return def;
  };

  const willDrawCell = (data: CellHookData) => {
    const raw = data.cell.raw;
    if (!raw || typeof raw !== 'object') return;
    const zeilen = hinterlegt.get(raw as object);
    if (!zeilen) return;
    const ab = ausschnittAb(zeilen.map((z) => z.text), data.cell.text);
    if (ab < 0) return;
    zuZeichnen.set(data.cell, zeilen.slice(ab, ab + data.cell.text.length));
    data.cell.text = [''];
  };

  const didDrawCell = (data: CellHookData) => {
    const teil = zuZeichnen.get(data.cell);
    if (!teil) return;
    const zelleJetzt = data.cell;
    const pt = zelleJetzt.styles.fontSize;
    const mm = pt / doc.internal.scaleFactor;
    const zeilenAbstand = mm * doc.getLineHeightFactor();
    const x = zelleJetzt.x + zelleJetzt.padding('left');
    // Grundlinie wie autoTable (jspdf-autotable autoTableText: y + fontSize·(2 − 1,15)).
    let y = zelleJetzt.y + zelleJetzt.padding('top') + mm * (2 - 1.15);
    for (const z of teil) {
      if (z.marke) {
        const farbe = ART_FARBE[z.marke].rgb;
        const bw = 3;
        const bh = mm * 1.08;
        const bx = zelleJetzt.x + 1.1;
        const by = y - mm * 0.84;
        doc.setFillColor(farbe[0], farbe[1], farbe[2]);
        doc.roundedRect(bx, by, bw, bh, 0.5, 0.5, 'F');
        setzeText(doc, pt - 1, 'bold', [255, 255, 255]);
        const kap = ((pt - 1) / doc.internal.scaleFactor) * 0.72;
        doc.text(z.marke, bx + bw / 2, by + bh / 2 + kap / 2, { align: 'center' });
      }
      if (z.text) {
        setzeText(doc, pt, z.ton === 'fett' ? 'bold' : 'normal', z.ton === 'grau' ? GRAU : TEXT);
        doc.text(z.text, x, y);
      }
      y += zeilenAbstand;
    }
  };

  return { zelle, willDrawCell, didDrawCell };
};

/** Schraeger, blasser Schriftzug in die Seitenmitte. */
const wasserzeichen = (doc: jsPDF, GState: GStateKlasse, text: string) => {
  const pt = 96;
  setzeText(doc, pt, 'bold', [100, 116, 139]);
  const w = doc.getTextWidth(text);
  const kap = (pt / doc.internal.scaleFactor) * 0.72;
  const winkel = 28;
  const rad = (winkel * Math.PI) / 180;
  const cx = SEITE_B / 2;
  const cy = SEITE_H / 2;
  // Startpunkt der Grundlinie so, dass die Mitte des gedrehten Schriftzugs auf der Seitenmitte liegt.
  const x = cx - (w / 2) * Math.cos(rad) + (kap / 2) * Math.sin(rad);
  const y = cy + (w / 2) * Math.sin(rad) + (kap / 2) * Math.cos(rad);
  doc.saveGraphicsState();
  doc.setGState(new GState({ opacity: 0.1 }));
  doc.text(text, x, y, { angle: winkel });
  doc.restoreGraphicsState();
};

/** Laufkopf (ab Seite 2), Fuss und ggf. Wasserzeichen auf jede Seite. */
const seitenAbschluss = (
  doc: jsPDF,
  GState: GStateKlasse,
  a: { laufkopf: string; laufkopfRechts: string; fussLinks: string; entwurf: boolean },
) => {
  const seiten = doc.getNumberOfPages();
  for (let i = 1; i <= seiten; i += 1) {
    doc.setPage(i);
    if (i > 1) {
      setzeText(doc, 7.5, 'normal', GRAU);
      doc.text(a.laufkopf, RAND, 8.5);
      if (a.laufkopfRechts) doc.text(a.laufkopfRechts, SEITE_B - RAND, 8.5, { align: 'right' });
      doc.setDrawColor(LINIE[0], LINIE[1], LINIE[2]);
      doc.setLineWidth(0.2);
      doc.line(RAND, 10.5, SEITE_B - RAND, 10.5);
    }
    const fussY = SEITE_H - 6.5;
    doc.setDrawColor(LINIE[0], LINIE[1], LINIE[2]);
    doc.setLineWidth(0.3);
    doc.line(RAND, fussY - 4, SEITE_B - RAND, fussY - 4);
    setzeText(doc, 7.5, 'normal', HELLGRAU);
    const links = doc.splitTextToSize(a.fussLinks, BREITE - 40) as string[];
    doc.text(links[0] ?? '', RAND, fussY);
    doc.text(`Seite ${i} von ${seiten}`, SEITE_B - RAND, fussY, { align: 'right' });
    if (a.entwurf) wasserzeichen(doc, GState, 'ENTWURF');
  }
};

/** Abschnittsueberschrift; bricht vorher um, wenn danach weniger als `platz` mm bleiben. */
const abschnitt = (doc: jsPDF, y: number, nr: number, titel: string, platz: number): number => {
  let yy = y;
  if (yy + platz > INHALT_UNTEN) {
    doc.addPage();
    yy = OBEN_FOLGESEITE;
  }
  doc.setFillColor(AKZENT[0], AKZENT[1], AKZENT[2]);
  doc.rect(RAND, yy + 0.4, 1.2, 4.4, 'F');
  setzeText(doc, 10, 'bold', TINTE);
  doc.text(`${nr}  ${titel}`, RAND + 3, yy + 4);
  return yy + 6.5;
};

const RASTER_STIL: Partial<Styles> = {
  font: 'helvetica',
  fontSize: 8,
  cellPadding: 1.4,
  textColor: TEXT,
  lineColor: LINIE,
  lineWidth: 0.15,
  valign: 'top',
  overflow: 'linebreak',
};

const LABEL_STIL: Partial<Styles> = {
  fontStyle: 'bold',
  fillColor: LABEL_FUELLUNG,
  textColor: GRAU,
  fontSize: 7.5,
};

/** Label/Wert-Raster: Paare in zwei Spaltenpaaren; ein Paar mit `breit` belegt die ganze Zeile. */
const angabenRaster = (
  doc: jsPDF,
  autoTable: AutoTable,
  y: number,
  paare: Array<{ label: string; wert: string; breit?: boolean }>,
  labelBreite = 34,
) => {
  const wertBreite = (BREITE - 2 * labelBreite) / 2;
  const body: RowInput[] = [];
  let offen: CellInput[] | null = null;
  for (const p of paare) {
    const wert = textNorm(p.wert) || '–';
    if (p.breit) {
      if (offen) { body.push([...offen, '', '']); offen = null; }
      body.push([p.label, { content: wert, colSpan: 3 }]);
    } else if (offen) {
      body.push([...offen, p.label, wert]);
      offen = null;
    } else {
      offen = [p.label, wert];
    }
  }
  if (offen) body.push([...offen, '', '']);
  autoTable(doc, {
    startY: y,
    margin: { left: RAND, right: RAND, top: OBEN_FOLGESEITE, bottom: UNTEN },
    theme: 'grid',
    body,
    styles: RASTER_STIL,
    columnStyles: {
      0: { ...LABEL_STIL, cellWidth: labelBreite },
      1: { cellWidth: wertBreite },
      2: { ...LABEL_STIL, cellWidth: labelBreite },
      3: { cellWidth: wertBreite },
    },
    rowPageBreak: 'avoid',
  });
  return endY(doc, y) + 5;
};

// ─── GBU ────────────────────────────────────────────────────────────────────

/** Spaltenbreiten der Gefaehrdungstabelle in mm; Massnahmen bekommen den Rest (Muster generatorUnterlage.ts). */
const SP = {
  nr: 8,
  faktor: 20,
  gefaehrdung: 38,
  folgen: 26,
  w: 6.5,
  s: 6.5,
  r: 7,
  verantwortlich: 22,
  termin: 17,
  vorsorge: 28,
};
const SP_MASSNAHMEN =
  BREITE - (SP.nr + SP.faktor + SP.gefaehrdung + SP.folgen + 2 * (SP.w + SP.s + SP.r) + SP.verantwortlich + SP.termin + SP.vorsorge);
const TAB_PT = 7;
const TAB_PAD = 1.2;
/** Linker Innenabstand der Massnahmenspalte: Platz fuer die Marke S/T/O/P. */
const MARKE_PAD = 4.8;

const zahlZelle = (n: number): CellDef => ({
  content: Number.isFinite(n) && n > 0 ? String(n) : '–',
  styles: { halign: 'center' },
});

const risikoZelle = (r: number): CellDef => {
  const stufe = risikoStufeVon(r);
  return {
    content: stufe ? String(r) : '–',
    styles: {
      halign: 'center',
      fontStyle: 'bold',
      textColor: TINTE,
      ...(stufe ? { fillColor: RISIKO_FARBE[stufe].rgb } : {}),
    },
  };
};

const KOPF_ZEILE_1: CellInput[] = [
  { content: 'Nr.', rowSpan: 2 },
  { content: 'Faktor', rowSpan: 2 },
  { content: 'Gefährdung / Belastung', rowSpan: 2 },
  { content: 'Mögliche Folgen', rowSpan: 2 },
  { content: 'Risiko vorher', colSpan: 3, styles: { halign: 'center' } },
  { content: 'Maßnahmen (S / T / O / P)', rowSpan: 2 },
  { content: 'Risiko nachher', colSpan: 3, styles: { halign: 'center' } },
  { content: 'Verantwortlich', rowSpan: 2 },
  { content: 'Termin', rowSpan: 2 },
  { content: 'Vorsorge (ArbMedVV)', rowSpan: 2 },
];
const KOPF_ZEILE_2: CellInput[] = ['W', 'S', 'R', 'W', 'S', 'R'].map((k) => ({ content: k, styles: { halign: 'center' } }));

/**
 * Eine Tabellenzeile je Gefaehrdung. Massnahmen, Verantwortlich und Termin
 * stehen blockweise auf gleicher Hoehe: jeder Block ist so hoch wie der
 * laengste seiner drei Teile, die kuerzeren werden mit Leerzeilen aufgefuellt.
 */
const gefaehrdungsZeile = (
  doc: jsPDF,
  reich: ReturnType<typeof reicheZellen>,
  g: GbuInhaltGefaehrdung,
): CellInput[] => {
  const br = (spalte: number, links = TAB_PAD) => spalte - links - TAB_PAD - 0.2;

  const gefZeilen: ReichZeile[] = [
    ...umbrechen(doc, textNorm(g.gefaehrdung) || '–', br(SP.gefaehrdung), TAB_PT).map((text): ReichZeile => ({ text, ton: 'normal' })),
    ...gefaehrdungZusatz(g).flatMap((z) => umbrechen(doc, z, br(SP.gefaehrdung), TAB_PT).map((text): ReichZeile => ({ text, ton: 'grau' }))),
  ];

  const massZeilen: ReichZeile[] = [];
  const verantZeilen: string[] = [];
  const terminZeilen: string[] = [];
  const massnahmen = g.massnahmen ?? [];
  if (massnahmen.length === 0) {
    massZeilen.push({ text: 'keine Maßnahme erfasst', ton: 'grau' });
  }
  for (const m of massnahmen) {
    const text = umbrechen(doc, massnahmeText(m) || '–', br(SP_MASSNAHMEN, MARKE_PAD), TAB_PT);
    const verant = umbrechen(doc, textNorm(m.verantwortlich) || '–', br(SP.verantwortlich), TAB_PT);
    const termin = umbrechen(doc, terminText(m.termin), br(SP.termin), TAB_PT);
    const hoehe = Math.max(text.length, verant.length, termin.length);
    for (let i = 0; i < hoehe; i += 1) {
      massZeilen.push({ text: text[i] ?? '', ton: 'normal', ...(i === 0 ? { marke: m.art } : {}) });
      verantZeilen.push(verant[i] ?? '');
      terminZeilen.push(termin[i] ?? '');
    }
  }

  const vorsZeilen: ReichZeile[] = [];
  for (const v of g.vorsorge ?? []) {
    vorsZeilen.push(...umbrechen(doc, VORSORGE_ART_LABEL[v.art] ?? v.art, br(SP.vorsorge), TAB_PT, 'bold').map((text): ReichZeile => ({ text, ton: 'fett' })));
    vorsZeilen.push(...umbrechen(doc, vorsorgeAnlassText(v), br(SP.vorsorge), TAB_PT).map((text): ReichZeile => ({ text, ton: 'normal' })));
  }
  if (vorsZeilen.length === 0) vorsZeilen.push({ text: '–', ton: 'grau' });

  return [
    { content: String(g.nr), styles: { halign: 'center', fontStyle: 'bold' } },
    umbrochen(doc, textNorm(g.faktorLabel) || textNorm(g.faktor), SP.faktor, TAB_PT),
    reich.zelle(gefZeilen),
    umbrochen(doc, textNorm(g.folgen) || '–', SP.folgen, TAB_PT),
    zahlZelle(g.wVor),
    zahlZelle(g.sVor),
    risikoZelle(g.rVor),
    reich.zelle(massZeilen),
    zahlZelle(g.wNach),
    zahlZelle(g.sNach),
    risikoZelle(g.rNach),
    verantZeilen.length ? verantZeilen.join('\n') : '',
    terminZeilen.length ? terminZeilen.join('\n') : '',
    reich.zelle(vorsZeilen),
  ];
};

/** Abschnitt 3: Matrix 3 × 3 links, Stufen und STOP rechts. */
const legende = (doc: jsPDF, autoTable: AutoTable, y: number): number => {
  const matrixBreite = 92;
  autoTable(doc, {
    startY: y,
    margin: { left: RAND, right: SEITE_B - RAND - matrixBreite, top: OBEN_FOLGESEITE, bottom: UNTEN },
    tableWidth: matrixBreite,
    theme: 'grid',
    head: [[
      { content: 'W \\ S', styles: { halign: 'left' } },
      ...S_STUFEN.map((s, i) => ({ content: `S ${i + 1} ${s}`, styles: { halign: 'center' as const } })),
    ]],
    body: W_STUFEN.map((w, wi) => [
      { content: `W ${wi + 1} ${w}`, styles: { fontStyle: 'bold' as const, textColor: GRAU } },
      ...[1, 2, 3].map((s) => {
        const r = (wi + 1) * s;
        const stufe = risikoStufeVon(r) as RisikoStufe;
        return { content: String(r), styles: { halign: 'center' as const, fontStyle: 'bold' as const, fillColor: RISIKO_FARBE[stufe].rgb } };
      }),
    ]),
    styles: { ...RASTER_STIL, fontSize: 7.5, cellPadding: 1.3 },
    headStyles: { fillColor: LABEL_FUELLUNG, textColor: TEXT, fontStyle: 'bold', fontSize: 7.5 },
    columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 20 }, 2: { cellWidth: 20 }, 3: { cellWidth: 20 } },
  });
  const matrixEnde = endY(doc, y);

  // Rechte Spalte
  const x = RAND + matrixBreite + 8;
  const breite = SEITE_B - RAND - x;
  let ry = y + 3.6;
  setzeText(doc, 8, 'bold', TINTE);
  doc.text('Risiko R = Wahrscheinlichkeit W × Schwere S, bewertet vor und nach den Maßnahmen', x, ry);
  ry += 5.2;
  let cx = x;
  for (const stufe of ['gering', 'mittel', 'hoch'] as const) {
    const farbe = RISIKO_FARBE[stufe].rgb;
    doc.setFillColor(farbe[0], farbe[1], farbe[2]);
    doc.roundedRect(cx, ry - 3, 9, 4, 0.6, 0.6, 'F');
    setzeText(doc, 7.5, 'bold', TINTE);
    doc.text(RISIKO_LEGENDE[stufe].bereich, cx + 4.5, ry, { align: 'center' });
    setzeText(doc, 7.5, 'normal', TEXT);
    doc.text(RISIKO_LEGENDE[stufe].text, cx + 11, ry);
    cx += 11 + doc.getTextWidth(RISIKO_LEGENDE[stufe].text) + 7;
  }
  ry += 5;
  setzeText(doc, 7.5, 'normal', GRAU);
  const sText = S_ERKLAERUNG.map((s, i) => `S ${i + 1} ${s}`).join('  ·  ');
  doc.text(doc.splitTextToSize(sText, breite) as string[], x, ry);
  ry += 6;
  // STOP als 2 × 2-Raster: S | T, darunter O | P.
  const halbe = breite / 2;
  (['S', 'T', 'O', 'P'] as const).forEach((art, i) => {
    const ax = x + (i % 2) * halbe;
    const ay = ry + Math.floor(i / 2) * 5;
    const farbe = ART_FARBE[art].rgb;
    doc.setFillColor(farbe[0], farbe[1], farbe[2]);
    doc.roundedRect(ax, ay - 3, 4, 4, 0.6, 0.6, 'F');
    setzeText(doc, 7, 'bold', [255, 255, 255]);
    doc.text(art, ax + 2, ay - 0.3, { align: 'center' });
    setzeText(doc, 7.5, 'normal', TEXT);
    doc.text((doc.splitTextToSize(ART_ERKLAERUNG[art], halbe - 8) as string[])[0] ?? '', ax + 5.5, ay);
  });
  ry += 5 + 4.8;
  setzeText(doc, 7.5, 'normal', GRAU);
  const stop = doc.splitTextToSize(STOP_HINWEIS, breite) as string[];
  doc.text(stop, x, ry);
  ry += (stop.length - 1) * 3.4 + 2;
  return Math.max(matrixEnde, ry) + 5;
};

/** Abschnitt 7: drei Kaesten nebeneinander. */
const freigabeBlock = (doc: jsPDF, y: number, felder: FreigabeFeld[]): number => {
  const abstand = 5;
  const kw = (BREITE - 2 * abstand) / 3;
  const kh = 40;
  felder.forEach((f, i) => {
    const x = RAND + i * (kw + abstand);
    doc.setDrawColor(LINIE[0], LINIE[1], LINIE[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, kw, kh, 1.2, 1.2, 'S');

    setzeText(doc, 9, 'bold', TINTE);
    doc.text(f.titel, x + 3, y + 5.5);
    if (f.hinweis) {
      const tb = doc.getTextWidth(f.titel);
      setzeText(doc, 7, 'normal', GRAU);
      const hinweis = doc.splitTextToSize(f.hinweis, kw - tb - 9) as string[];
      doc.text(hinweis[0] ?? '', x + 3 + tb + 2.5, y + 5.5);
    }

    const labelX = x + 3;
    const wertX = x + 21;
    const linieEnde = x + kw - 3;
    const zeile = (label: string, wert: string, yy: number, linie: boolean) => {
      setzeText(doc, 7, 'normal', GRAU);
      doc.text(label, labelX, yy);
      if (wert) {
        setzeText(doc, 8.5, 'normal', TEXT);
        doc.text((doc.splitTextToSize(wert, linieEnde - wertX) as string[])[0] ?? '', wertX, yy);
      } else if (linie) {
        doc.setDrawColor(LINIE_DUNKEL[0], LINIE_DUNKEL[1], LINIE_DUNKEL[2]);
        doc.setLineWidth(0.25);
        doc.line(wertX, yy + 0.8, linieEnde, yy + 0.8);
      }
    };
    const leer = f.zustand === 'leer';
    zeile('Name', f.zustand === 'entfaellt' ? '–' : f.name, y + 12, leer);
    zeile('Funktion', f.zustand === 'entfaellt' ? '–' : f.funktion, y + 17, leer);
    zeile('Datum', f.datum, y + 22, leer);

    // Unterschrift: Bild ueber der Linie, darunter das Label.
    const linieY = y + kh - 5.5;
    const unterschriftLinie = f.zustand === 'leer' ? LINIE_DUNKEL : LINIE;
    doc.setDrawColor(unterschriftLinie[0], unterschriftLinie[1], unterschriftLinie[2]);
    doc.setLineWidth(0.3);
    doc.line(wertX, linieY, linieEnde, linieY);
    setzeText(doc, 7, 'normal', GRAU);
    doc.text('Unterschrift', labelX, linieY);

    const flaecheOben = y + 24;
    const flaecheH = linieY - flaecheOben - 0.6;
    const flaecheB = linieEnde - wertX;
    let gezeichnet = false;
    if (f.bild) {
      const format = bildFormat(f.bild);
      if (format) {
        try {
          const p = doc.getImageProperties(f.bild);
          const faktor = Math.min(flaecheB / p.width, flaecheH / p.height);
          const bw = p.width * faktor;
          const bh = p.height * faktor;
          // 'FAST': jsPDF legt PNGs sonst unkomprimiert ab (je Unterschrift einige 100 KB).
          doc.addImage(f.bild, format, wertX, linieY - 0.6 - bh, bw, bh, undefined, 'FAST');
          gezeichnet = true;
        } catch {
          // Ein unlesbares Bild darf das PDF nicht verhindern — dann steht der Hinweis da.
        }
      }
    }
    if (!gezeichnet) {
      const hinweis =
        f.zustand === 'unterschrieben' ? (f.weg === 'papier' ? 'auf Papier unterschrieben' : 'unterschrieben (ohne Bild)')
          : f.zustand === 'offen' ? 'noch nicht unterschrieben'
            : f.zustand === 'entfaellt' ? 'nicht vorgesehen'
              : '';
      if (hinweis) {
        setzeText(doc, 7.5, 'italic', HELLGRAU);
        doc.text(hinweis, wertX, linieY - 2);
      }
    }
    if (f.zustand === 'unterschrieben' && f.weg) {
      setzeText(doc, 6.5, 'normal', HELLGRAU);
      doc.text(WEG_TEXT[f.weg], linieEnde, y + kh - 1.8, { align: 'right' });
    }
  });
  return y + kh + 5;
};

/**
 * Die GBU als PDF (A4 quer). `opt.unterschriften` zeigt die Unterschriften
 * im Freigabeblock, `leereUnterschriftsfelder` druckt Linien fuer den
 * Papierweg, `entwurf` setzt „ENTWURF" schraeg ueber jede Seite.
 */
export const erzeugeGbuPdf = async (inhalt: GbuFassungInhalt, opt: GbuAusgabeOptionen = {}): Promise<Blob> => {
  const { doc, autoTable, GState } = await ladePdf();
  const reich = reicheZellen(doc);
  const kunde = t(inhalt.kunde?.name);
  const buero = t(inhalt.buero?.name);
  const titel = textNorm(inhalt.titel) || textNorm(inhalt.bereich?.bezeichnung) || 'Gefährdungsbeurteilung';
  doc.setProperties({ title: `Gefährdungsbeurteilung Nr. ${inhalt.nummer} – ${titel}`, subject: kunde, creator: buero });

  // ── Kopf ──────────────────────────────────────────────────────────────
  let y = 15;
  setzeText(doc, 17, 'bold', TINTE);
  doc.text(`Gefährdungsbeurteilung Nr. ${inhalt.nummer}`, RAND, y);
  if (buero) {
    setzeText(doc, 9, 'bold', TEXT);
    doc.text(buero, SEITE_B - RAND, y - 5, { align: 'right' });
  }
  setzeText(doc, 8.5, 'normal', GRAU);
  doc.text(`Fassung ${inhalt.fassung} · erstellt ${datumDe(inhalt.erstelltAm) || '–'}`, SEITE_B - RAND, y, { align: 'right' });
  y += 6;
  setzeText(doc, 10.5, 'normal', AKZENT);
  doc.text((doc.splitTextToSize(`${titel}${kunde ? ` · ${kunde}` : ''}`, BREITE) as string[])[0] ?? '', RAND, y);
  y += 3.2;
  doc.setDrawColor(AKZENT[0], AKZENT[1], AKZENT[2]);
  doc.setLineWidth(0.6);
  doc.line(RAND, y, SEITE_B - RAND, y);
  y += 4;

  // ── 1 Allgemeine Angaben ──────────────────────────────────────────────
  const bereich = inhalt.bereich ?? { bezeichnung: '', bereich: null, beschreibung: null, ort: null };
  const kundeText = [kunde, textNorm(inhalt.kunde?.anschrift)].filter(Boolean).join('\n');
  const bereichText = [textNorm(bereich.bezeichnung), t(bereich.bereich) ? `(${t(bereich.bereich)})` : ''].filter(Boolean).join(' ');
  y = abschnitt(doc, y, 1, 'Allgemeine Angaben', 30);
  y = angabenRaster(doc, autoTable, y, [
    { label: 'Kunde / Betrieb', wert: kundeText },
    { label: 'Bereich / Tätigkeit', wert: bereichText },
    { label: 'Anlass', wert: ANLASS_LABEL[inhalt.anlass] ?? t(inhalt.anlass) },
    { label: 'Ort', wert: t(bereich.ort) },
    { label: 'Erstellt am', wert: datumDe(inhalt.erstelltAm) },
    { label: 'Prüfung bis', wert: datumDe(inhalt.pruefenBis) },
    ...(textNorm(bereich.beschreibung) ? [{ label: 'Beschreibung der Tätigkeit', wert: textNorm(bereich.beschreibung), breit: true }] : []),
    { label: 'Beteiligte', wert: t(inhalt.kopf?.beteiligte), breit: true },
    { label: 'Unterlagen', wert: t(inhalt.kopf?.unterlagen), breit: true },
  ]);

  // ── 2 Rechtsgrundlagen und Rahmenbedingungen ──────────────────────────
  const vorschriften = (inhalt.kopf?.vorschriften ?? []).map(t).filter(Boolean);
  y = abschnitt(doc, y, 2, 'Rechtsgrundlagen und Rahmenbedingungen', 24);
  y = angabenRaster(doc, autoTable, y, [
    { label: 'Rechtsgrundlagen und Regelwerke', wert: vorschriften.join('  ·  '), breit: true },
    { label: 'PSA-Grundausstattung', wert: t(inhalt.kopf?.psaGrundausstattung), breit: true },
    { label: 'Randbedingungen', wert: t(inhalt.kopf?.randbedingungen), breit: true },
    { label: 'Umwelt', wert: t(inhalt.kopf?.umwelt), breit: true },
    { label: 'Koordination', wert: t(inhalt.kopf?.koordination), breit: true },
  ]);

  // ── 3 Risikobewertung ─────────────────────────────────────────────────
  y = abschnitt(doc, y, 3, 'Risikobewertung und Rangfolge der Maßnahmen', 34);
  y = legende(doc, autoTable, y);

  // ── 4 Gefaehrdungen und Massnahmen ────────────────────────────────────
  y = abschnitt(doc, y, 4, 'Gefährdungen und Maßnahmen', 32);
  const gefaehrdungen = [...(inhalt.gefaehrdungen ?? [])].sort((a, b) => a.nr - b.nr);
  const body: RowInput[] = gefaehrdungen.length
    ? gefaehrdungen.map((g) => gefaehrdungsZeile(doc, reich, g))
    : [[{ content: 'Keine Gefährdungen erfasst.', colSpan: 14, styles: { textColor: GRAU, fontStyle: 'italic' } }]];
  autoTable(doc, {
    startY: y,
    margin: { left: RAND, right: RAND, top: OBEN_FOLGESEITE, bottom: UNTEN },
    theme: 'grid',
    head: [KOPF_ZEILE_1, KOPF_ZEILE_2],
    body,
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    styles: { ...RASTER_STIL, fontSize: TAB_PT, cellPadding: TAB_PAD },
    headStyles: {
      fillColor: KOPF_FUELLUNG,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: TAB_PT,
      valign: 'middle',
      lineColor: [120, 120, 120],
      lineWidth: 0.15,
    },
    columnStyles: {
      0: { cellWidth: SP.nr },
      1: { cellWidth: SP.faktor },
      2: { cellWidth: SP.gefaehrdung },
      3: { cellWidth: SP.folgen },
      4: { cellWidth: SP.w },
      5: { cellWidth: SP.s },
      6: { cellWidth: SP.r },
      7: { cellWidth: SP_MASSNAHMEN, cellPadding: { top: TAB_PAD, bottom: TAB_PAD, left: MARKE_PAD, right: TAB_PAD } },
      8: { cellWidth: SP.w },
      9: { cellWidth: SP.s },
      10: { cellWidth: SP.r },
      11: { cellWidth: SP.verantwortlich },
      12: { cellWidth: SP.termin },
      13: { cellWidth: SP.vorsorge },
    },
    willDrawCell: reich.willDrawCell,
    didDrawCell: reich.didDrawCell,
  });
  y = endY(doc, y) + 3;

  const hoch = restrisikoHoch(inhalt);
  if (hoch.length) {
    if (y + 9 > INHALT_UNTEN) { doc.addPage(); y = OBEN_FOLGESEITE; }
    const text = `Restrisiko hoch (R 6–9) nach den Maßnahmen bei Nr. ${hoch.join(', ')} – weitere Maßnahmen prüfen.`;
    doc.setFillColor(ROT_HELL[0], ROT_HELL[1], ROT_HELL[2]);
    doc.setDrawColor(LINIE[0], LINIE[1], LINIE[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(RAND, y, BREITE, 7, 1, 1, 'FD');
    setzeText(doc, 8, 'bold', ROT_TEXT);
    doc.text(text, RAND + 3, y + 4.6);
    y += 7;
  }
  y += 5;

  // ── 5 Grundlegende organisatorische Massnahmen ────────────────────────
  const org = (inhalt.orgMassnahmen ?? []).map(textNorm).filter(Boolean);
  y = abschnitt(doc, y, 5, 'Grundlegende organisatorische Maßnahmen', 18);
  autoTable(doc, {
    startY: y,
    margin: { left: RAND, right: RAND, top: OBEN_FOLGESEITE, bottom: UNTEN },
    theme: 'grid',
    body: org.length
      ? org.map((m, i) => [{ content: `${i + 1}.`, styles: { halign: 'right' as const, textColor: GRAU } }, m])
      : [[{ content: 'Keine grundlegenden organisatorischen Maßnahmen erfasst.', colSpan: 2, styles: { textColor: GRAU, fontStyle: 'italic' as const } }]],
    styles: RASTER_STIL,
    columnStyles: { 0: { cellWidth: 9 }, 1: { cellWidth: BREITE - 9 } },
    rowPageBreak: 'avoid',
  });
  y = endY(doc, y) + 5;

  // ── 6 Notfall und Erste Hilfe ─────────────────────────────────────────
  const nf = inhalt.notfall ?? { ersteHilfe: '', brand: '', notruf: '', zugaenge: '', meldung: '', kontakte: '' };
  y = abschnitt(doc, y, 6, 'Notfall und Erste Hilfe', 22);
  y = angabenRaster(doc, autoTable, y, [
    { label: 'Erste Hilfe', wert: t(nf.ersteHilfe) },
    { label: 'Brand', wert: t(nf.brand) },
    { label: 'Notruf', wert: t(nf.notruf) },
    { label: 'Zugänge', wert: t(nf.zugaenge) },
    { label: 'Meldung', wert: t(nf.meldung) },
    { label: 'Kontakte', wert: t(nf.kontakte) },
  ]);

  // ── 7 Freigabe ────────────────────────────────────────────────────────
  y = abschnitt(doc, y, 7, 'Freigabe', 6.5 + 40 + 9);
  y = freigabeBlock(doc, y, freigabeFelder(inhalt, opt));
  const pruef = datumDe(inhalt.pruefenBis);
  setzeText(doc, 7.5, 'normal', GRAU);
  doc.text(
    `Die Gefährdungsbeurteilung ist bei Änderungen der Tätigkeit, nach Unfällen oder Beinaheunfällen und spätestens bis ${pruef || 'zum Prüftermin'} zu überprüfen.`,
    RAND,
    y,
  );

  seitenAbschluss(doc, GState, {
    laufkopf: `Gefährdungsbeurteilung Nr. ${inhalt.nummer} · ${titel}`,
    laufkopfRechts: buero,
    fussLinks: [`GBU Nr. ${inhalt.nummer}`, `Fassung ${inhalt.fassung}`, kunde].filter(Boolean).join(' · '),
    entwurf: !!opt.entwurf,
  });
  return doc.output('blob');
};

// ─── Massnahmenliste ────────────────────────────────────────────────────────

const STAND_FARBE: Record<MassnahmeListenZeile['stand'], Rgb> = {
  offen: TEXT,
  gemeldet: [180, 83, 9],
  wirksam: [21, 128, 61],
  unwirksam: ROT_TEXT,
};

const LISTE_PT = 7.5;
const LISTE_PAD = 1.4;

/** Kurzzahlen fuer die Zeile unter der Ueberschrift. */
export const massnahmenZaehler = (zeilen: readonly MassnahmeListenZeile[]) => ({
  gesamt: zeilen.length,
  offen: zeilen.filter((z) => z.stand === 'offen').length,
  gemeldet: zeilen.filter((z) => z.stand === 'gemeldet').length,
  wirksam: zeilen.filter((z) => z.stand === 'wirksam').length,
  unwirksam: zeilen.filter((z) => z.stand === 'unwirksam').length,
  ueberfaellig: zeilen.filter((z) => z.ueberfaellig).length,
});

/** Massnahmen- und Terminliste (A4 quer) ueber alle uebergebenen Zeilen; ueberfaellige rot. */
export const erzeugeMassnahmenlistePdf = async (zeilen: readonly MassnahmeListenZeile[], meta: MassnahmenlisteMeta): Promise<Blob> => {
  const { doc, autoTable, GState } = await ladePdf();
  const kunde = t(meta.kundeName);
  const buero = t(meta.bueroName);
  const stand = datumDe(meta.stand);
  doc.setProperties({ title: `Maßnahmen- und Terminliste${kunde ? ` – ${kunde}` : ''}`, subject: kunde, creator: buero });

  let y = 15;
  setzeText(doc, 17, 'bold', TINTE);
  doc.text('Maßnahmen- und Terminliste', RAND, y);
  if (buero) {
    setzeText(doc, 9, 'bold', TEXT);
    doc.text(buero, SEITE_B - RAND, y - 5, { align: 'right' });
  }
  setzeText(doc, 8.5, 'normal', GRAU);
  doc.text(`Stand ${stand || '–'}`, SEITE_B - RAND, y, { align: 'right' });
  y += 6;
  setzeText(doc, 10.5, 'normal', AKZENT);
  doc.text([kunde, t(meta.untertitel)].filter(Boolean).join(' · ') || 'Gefährdungsbeurteilungen', RAND, y);
  y += 3.2;
  doc.setDrawColor(AKZENT[0], AKZENT[1], AKZENT[2]);
  doc.setLineWidth(0.6);
  doc.line(RAND, y, SEITE_B - RAND, y);
  y += 5.5;

  const z = massnahmenZaehler(zeilen);
  setzeText(doc, 8.5, 'normal', TEXT);
  const teile = [
    `${z.gesamt} ${z.gesamt === 1 ? 'Maßnahme' : 'Maßnahmen'}`,
    `${z.offen} offen`,
    `${z.gemeldet} umgesetzt gemeldet`,
    `${z.wirksam} wirksam`,
    ...(z.unwirksam ? [`${z.unwirksam} unwirksam`] : []),
  ];
  doc.text(teile.join('  ·  '), RAND, y);
  if (z.ueberfaellig) {
    const w = doc.getTextWidth(`${teile.join('  ·  ')}  ·  `);
    setzeText(doc, 8.5, 'bold', ROT_TEXT);
    doc.text(`${z.ueberfaellig} überfällig`, RAND + w, y);
  }
  y += 4;

  const B = { gbu: 32, bereich: 26, gefaehrdung: 48, art: 9, verantwortlich: 28, termin: 20, stand: 24 };
  const massBreite = BREITE - (B.gbu + B.bereich + B.gefaehrdung + B.art + B.verantwortlich + B.termin + B.stand);
  const body: RowInput[] = zeilen.length
    ? zeilen.map((zl): CellInput[] => {
      const rot: Partial<Styles> = zl.ueberfaellig ? { fillColor: ROT_HELL } : {};
      const zelle = (text: string, breite: number) => umbrochen(doc, text || '–', breite, LISTE_PT, 'normal', LISTE_PAD);
      return [
        { content: `Nr. ${zl.gbuNummer}\n${zelle(textNorm(zl.gbuTitel), B.gbu)}`, styles: rot },
        { content: zelle(textNorm(zl.bereich), B.bereich), styles: rot },
        { content: zelle(textNorm(zl.gefaehrdung), B.gefaehrdung), styles: rot },
        { content: zl.art, styles: { halign: 'center', fontStyle: 'bold', textColor: [255, 255, 255], fillColor: ART_FARBE[zl.art]?.rgb ?? GRAU } },
        { content: zelle(textNorm(zl.text), massBreite), styles: rot },
        { content: zelle(textNorm(zl.verantwortlich), B.verantwortlich), styles: rot },
        {
          content: zl.ueberfaellig ? `${terminText(zl.termin)}\nüberfällig` : terminText(zl.termin),
          styles: { ...rot, ...(zl.ueberfaellig ? { textColor: ROT_TEXT, fontStyle: 'bold' } : {}) },
        },
        { content: STAND_LABEL[zl.stand] ?? zl.stand, styles: { ...rot, textColor: STAND_FARBE[zl.stand] ?? TEXT, fontStyle: 'bold' } },
      ];
    })
    : [[{ content: 'Keine Maßnahmen erfasst.', colSpan: 8, styles: { textColor: GRAU, fontStyle: 'italic' } }]];

  autoTable(doc, {
    startY: y,
    margin: { left: RAND, right: RAND, top: OBEN_FOLGESEITE, bottom: UNTEN },
    theme: 'grid',
    head: [['GBU', 'Bereich', 'Gefährdung', { content: 'Art', styles: { halign: 'center' } }, 'Maßnahme', 'Verantwortlich', 'Termin', 'Stand']],
    body,
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    styles: { ...RASTER_STIL, fontSize: LISTE_PT, cellPadding: LISTE_PAD },
    headStyles: { fillColor: KOPF_FUELLUNG, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: LISTE_PT, valign: 'middle' },
    columnStyles: {
      0: { cellWidth: B.gbu },
      1: { cellWidth: B.bereich },
      2: { cellWidth: B.gefaehrdung },
      3: { cellWidth: B.art },
      4: { cellWidth: massBreite },
      5: { cellWidth: B.verantwortlich },
      6: { cellWidth: B.termin },
      7: { cellWidth: B.stand },
    },
  });

  seitenAbschluss(doc, GState, {
    laufkopf: `Maßnahmen- und Terminliste${kunde ? ` · ${kunde}` : ''}`,
    laufkopfRechts: buero,
    fussLinks: ['Maßnahmenliste', kunde, stand ? `Stand ${stand}` : ''].filter(Boolean).join(' · '),
    entwurf: false,
  });
  return doc.output('blob');
};
